import { Dialog, DialogContent, DialogTitle, Stack } from '@mui/material';
import { AnimatePresence, motion } from 'framer-motion';
import type React from 'react';
import { useEffect } from 'react';
import { BACKDROP_STYLE } from '../../../config/styles';
import { useIsMobile } from '../../../hooks';
import { ThemeProvider } from '../../../theme-minimals';
import { MobileSwipeableDrawer } from '../../../index';
import { AppointmentStep1 } from './appointment-step1';
import { AppointmentStep2 } from './appointment-step2';
import { AppointmentStep3 } from './appointment-step3';
import {
  TAppointmentDateTimeSlot,
  TAppointmentModalState,
  TAppointmentProjectMinimal,
} from './types';

import { useAppointmentModalStore } from './hooks/use-appointment-modal-store';
import { useAppointmentModalActions, useStep } from './store';

export type AppointmentCreationState = 'idle' | 'creating' | 'success';

interface AppointmentModalProps {
  isOpen: boolean;
  onClose: (stepNr?: number) => void;
  dateTimeSlots?: TAppointmentDateTimeSlot[];
  fetchTimeSlotsForProject?: (projectId: number) => Promise<TAppointmentDateTimeSlot[]>;
  creationState?: AppointmentCreationState;
  initialData: TAppointmentModalState;
  projects: TAppointmentProjectMinimal[];
  onBookAppointment: (data: TAppointmentModalState) => void;
  onStep1Continue?: () => void;
  t: (key: string) => string;
  warningBanner?: React.ReactNode;
  hideDuration?: boolean;
  direction?: 'ltr' | 'rtl';
  logoSrc?: string;
  locale?: string;
  isLanding?: boolean;
}

export const AppointmentModal: React.FC<AppointmentModalProps> = ({
  isOpen,
  onClose,
  onBookAppointment,
  onStep1Continue,
  dateTimeSlots,
  fetchTimeSlotsForProject,
  creationState = 'idle',
  initialData,
  projects,
  t,
  warningBanner,
  hideDuration = false,
  direction = 'ltr',
  logoSrc,
  locale,
  isLanding = false,
}) => {
  const isMobile = useIsMobile();
  useAppointmentModalStore(initialData, dateTimeSlots, fetchTimeSlotsForProject, locale);
  const step = useStep();

  const { setIsOpen, setStep } = useAppointmentModalActions();

  useEffect(() => {
    setIsOpen(isOpen); // track isOpen state
  }, [isOpen]);

  // Handle moving to success step when creation is successful
  useEffect(() => {
    if (creationState === 'success' && step === 2) {
      setStep(3);
    }
  }, [creationState, step]);

  const handleClose = (stepNr?: number) => {
    onClose(stepNr);
    setStep(1);
  };

  // Content component that can be used both in modal and inline
  const appointmentContent = (
    <AnimatePresence mode="wait" initial={false}>
      {step === 1 && (
        <motion.div
          key="step1"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{
            duration: 0.25,
            ease: [0.25, 0.46, 0.45, 0.94],
          }}
          style={{ minHeight: '400px' }} // Ensure minimum height for smoother transitions
        >
          <AppointmentStep1
            projects={projects}
            onClose={handleClose}
            onContinue={onStep1Continue}
            t={t}
            warningBanner={warningBanner}
            hideDuration={hideDuration}
            logoSrc={logoSrc}
            isLanding={isLanding}
          />
        </motion.div>
      )}

      {step === 2 && (
        <motion.div
          key="step2"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{
            duration: 0.25,
            ease: [0.25, 0.46, 0.45, 0.94],
          }}
          style={{ minHeight: '400px' }} // Ensure minimum height for smoother transitions
        >
          <AppointmentStep2
            onBookAppointment={onBookAppointment}
            onClose={handleClose}
            t={t}
            isCreating={creationState === 'creating'}
            isEditMode={!!initialData?.name && !!initialData?.email && !!initialData?.phone}
            logoSrc={logoSrc}
            locale={locale}
            isLanding={isLanding}
            direction={direction}
          />
        </motion.div>
      )}

      {step === 3 && (
        <motion.div
          key="step3"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          transition={{
            duration: 0.4,
            ease: [0.25, 0.46, 0.45, 0.94],
          }}
          style={{
            minHeight: '245px', // Smaller min height for success step
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <AppointmentStep3 onContinue={handleClose} t={t} />
        </motion.div>
      )}
    </AnimatePresence>
  );

  return isMobile ? (
    <ThemeProvider language={direction === 'rtl' ? 'he' : 'en'}>
      <MobileSwipeableDrawer
        isOpen={isOpen}
        onClose={() => {
          handleClose(step);
        }}
        hideBackdrop={false}
        zIndex={9999}
        containerSx={{ overflow: 'auto' }}
      >
        <Stack
          sx={{
            overflow: 'hidden',
            background: '#fff',
            padding: 2,
            paddingBottom: 6,
          }}
        >
          <div dir={direction} style={{ width: '100%' }}>
            {appointmentContent}
          </div>
        </Stack>
      </MobileSwipeableDrawer>
    </ThemeProvider>
  ) : (
    <ThemeProvider language={direction === 'rtl' ? 'he' : 'en'}>
      <Dialog
        open={isOpen}
        dir={direction}
        onClose={() => {
          handleClose(step);
        }}
        maxWidth="sm"
        fullWidth
        sx={{
          '& .MuiBackdrop-root': BACKDROP_STYLE,
          '& .MuiDialog-paper': {
            maxWidth: '512px',
          },
          zIndex: 9999,
        }}
      >
        <DialogTitle sx={{ display: 'none' }}>
          {!!initialData
            ? t('scheduling.appointment.editAppointment')
            : t('scheduling.appointment.bookOnlineTour')}
        </DialogTitle>
        <DialogContent
          sx={{
            position: 'relative',
            overflow: 'auto',
            p: 4,
            pt: '24px !important',
          }}
        >
          {appointmentContent}
        </DialogContent>
      </Dialog>
    </ThemeProvider>
  );
};
